import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Store logout in ref for interceptor access
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Setup axios interceptor for 401 errors - only logout on specific auth failures
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Only force logout for explicit credential/token validation errors, not general 401s
        const detail = error.response?.data?.detail?.toLowerCase() || '';
        const isAuthError = error.response?.status === 401 && 
          (detail.includes('could not validate credentials') || 
           detail.includes('token expired') ||
           detail.includes('invalid token'));
        
        if (isAuthError) {
          console.log('[Auth] Token invalid, logging out');
          if (logoutRef.current) {
            logoutRef.current();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout if it's an auth error, not a network error
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
      // For network errors, keep the user logged in with stored token
      // but clear loading state
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      
      // Safety timeout - ensure loading state clears even if fetch hangs
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 10000);
      
      return () => clearTimeout(timeout);
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    try {
      console.log('[Auth] Attempting login for:', email);
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      if (!access_token) {
        console.error('[Auth] No access token in response');
        throw new Error('No access token received');
      }
      
      console.log('[Auth] Login successful, setting token');
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      console.log('[Auth] User set:', userData?.email, 'Role:', userData?.role);
      return userData;
    } catch (error) {
      console.error('[Auth] Login failed:', error);
      throw error;
    }
  };

  // Set auth state directly (for post-registration or portal login)
  const setAuth = (userData, accessToken) => {
    if (!accessToken || !userData) {
      console.error('[Auth] setAuth called without valid data');
      return;
    }
    console.log('[Auth] Setting auth state directly for:', userData?.email);
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
  };

  const register = async (email, password, fullName, role, teamName = '') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      role,
      team_name: teamName || undefined
    });
    const { access_token, user: userData, child_id } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    // Return both user and child_id for athletes
    return { user: userData, child_id };
  };

  // Helper functions to check user roles
  const isEppStaff = () => {
    const role = user?.role;
    return role === 'coach' || role === 'epp_staff' || role === 'admin';
  };

  const isTeamManager = () => {
    return user?.role === 'team_manager';
  };

  const isViewOnly = () => {
    return user?.role === 'team_manager';
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isEppStaff,
    isTeamManager,
    isViewOnly,
    login,
    setAuth,
    register,
    logout,
    fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
